// src/components/forms/columnBuilder.tsx
import React, { JSX } from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { TableField, TenantFlagRule } from "../../types/interfaces";
import { formatFieldValue } from "./editorUtils";
import { getBlobLabel, getMimeTypeFromExtension } from "../../utils/layoutUtils";
import { Tooltip, Button, Box, IconButton } from "@mui/material";
import RowActionsMenu from "../RowActionsMenu";

import AccessTimeIcon from "@mui/icons-material/AccessTime";


// Import our flag icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import StarIcon from "@mui/icons-material/Star";
import FlagIcon from "@mui/icons-material/Flag";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloudIcon from "@mui/icons-material/Cloud";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventIcon from "@mui/icons-material/Event";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import HelpIcon from "@mui/icons-material/Help";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import BuildIcon from "@mui/icons-material/Build";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ChatIcon from "@mui/icons-material/Chat";
import CodeIcon from "@mui/icons-material/Code";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GroupIcon from "@mui/icons-material/Group";
import HistoryIcon from "@mui/icons-material/History";
import LabelIcon from "@mui/icons-material/Label";
import LanguageIcon from "@mui/icons-material/Language";
import ListIcon from "@mui/icons-material/List";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import PrintIcon from "@mui/icons-material/Print";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WorkIcon from "@mui/icons-material/Work";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CallIcon from "@mui/icons-material/Call";
import DirectionsIcon from "@mui/icons-material/Directions";
import EmailIcon from "@mui/icons-material/Email";
import ExtensionIcon from "@mui/icons-material/Extension";
import FaceIcon from "@mui/icons-material/Face";
import FilterListIcon from "@mui/icons-material/FilterList";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import HeadsetIcon from "@mui/icons-material/Headset";
import InvertColorsIcon from "@mui/icons-material/InvertColors";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import MapIcon from "@mui/icons-material/Map";
import NavigationIcon from "@mui/icons-material/Navigation";
import PaletteIcon from "@mui/icons-material/Palette";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import RadioIcon from "@mui/icons-material/Radio";
import SchoolIcon from "@mui/icons-material/School";
import TerrainIcon from "@mui/icons-material/Terrain";
import UsbIcon from "@mui/icons-material/Usb";
import VideocamIcon from "@mui/icons-material/Videocam";
import WatchIcon from "@mui/icons-material/Watch";
import WifiIcon from "@mui/icons-material/Wifi";

export const iconMap: Record<string, JSX.Element> = {
    "check_circle": <CheckCircleIcon fontSize="small" />,
    "error": <ErrorIcon fontSize="small" />,
    "warning": <WarningIcon fontSize="small" />,
    "info": <InfoIcon fontSize="small" />,
    "star": <StarIcon fontSize="small" />,
    "flag": <FlagIcon fontSize="small" />,
    "lock": <LockIcon fontSize="small" />,
    "visibility": <VisibilityIcon fontSize="small" />,
    "edit": <EditIcon fontSize="small" />,
    "delete": <DeleteIcon fontSize="small" />,
    "add": <AddIcon fontSize="small" />,
    "remove": <RemoveIcon fontSize="small" />,
    "cloud": <CloudIcon fontSize="small" />,
    "download": <DownloadIcon fontSize="small" />,
    "upload": <UploadIcon fontSize="small" />,
    "schedule": <ScheduleIcon fontSize="small" />,
    "event": <EventIcon fontSize="small" />,
    "home": <HomeIcon fontSize="small" />,
    "settings": <SettingsIcon fontSize="small" />,
    "search": <SearchIcon fontSize="small" />,
    "account_circle": <AccountCircleIcon fontSize="small" />,
    "shopping_cart": <ShoppingCartIcon fontSize="small" />,
    "thumb_up": <ThumbUpIcon fontSize="small" />,
    "thumb_down": <ThumbDownIcon fontSize="small" />,
    "help": <HelpIcon fontSize="small" />,
    "announcement": <AnnouncementIcon fontSize="small" />,
    "build": <BuildIcon fontSize="small" />,
    "camera_alt": <CameraAltIcon fontSize="small" />,
    "chat": <ChatIcon fontSize="small" />,
    "code": <CodeIcon fontSize="small" />,
    "dashboard": <DashboardIcon fontSize="small" />,
    "favorite": <FavoriteIcon fontSize="small" />,
    "group": <GroupIcon fontSize="small" />,
    "history": <HistoryIcon fontSize="small" />,
    "label": <LabelIcon fontSize="small" />,
    "language": <LanguageIcon fontSize="small" />,
    "list": <ListIcon fontSize="small" />,
    "location_on": <LocationOnIcon fontSize="small" />,
    "menu": <MenuIcon fontSize="small" />,
    "notifications": <NotificationsIcon fontSize="small" />,
    "person": <PersonIcon fontSize="small" />,
    "phone": <PhoneIcon fontSize="small" />,
    "print": <PrintIcon fontSize="small" />,
    "save": <SaveIcon fontSize="small" />,
    "send": <SendIcon fontSize="small" />,
    "share": <ShareIcon fontSize="small" />,
    "visibility_off": <VisibilityOffIcon fontSize="small" />,
    "work": <WorkIcon fontSize="small" />,
    "zoom_in": <ZoomInIcon fontSize="small" />,
    "zoom_out": <ZoomOutIcon fontSize="small" />,
    "attach_file": <AttachFileIcon fontSize="small" />,
    "battery_full": <BatteryFullIcon fontSize="small" />,
    "bookmark": <BookmarkIcon fontSize="small" />,
    "brightness_4": <Brightness4Icon fontSize="small" />,
    "calendar_today": <CalendarTodayIcon fontSize="small" />,
    "call": <CallIcon fontSize="small" />,
    "directions": <DirectionsIcon fontSize="small" />,
    "email": <EmailIcon fontSize="small" />,
    "extension": <ExtensionIcon fontSize="small" />,
    "face": <FaceIcon fontSize="small" />,
    "filter_list": <FilterListIcon fontSize="small" />,
    "gps_fixed": <GpsFixedIcon fontSize="small" />,
    "headset": <HeadsetIcon fontSize="small" />,
    "invert_colors": <InvertColorsIcon fontSize="small" />,
    "keyboard": <KeyboardIcon fontSize="small" />,
    "lightbulb": <LightbulbIcon fontSize="small" />,
    "map": <MapIcon fontSize="small" />,
    "navigation": <NavigationIcon fontSize="small" />,
    "palette": <PaletteIcon fontSize="small" />,
    "queue_music": <QueueMusicIcon fontSize="small" />,
    "radio": <RadioIcon fontSize="small" />,
    "school": <SchoolIcon fontSize="small" />,
    "terrain": <TerrainIcon fontSize="small" />,
    "usb": <UsbIcon fontSize="small" />,
    "videocam": <VideocamIcon fontSize="small" />,
    "watch": <WatchIcon fontSize="small" />,
    "wifi": <WifiIcon fontSize="small" />
  };
  
  const getForegroundColor = (hexColor: string): string => {
    const c = hexColor.replace("#", "");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };
  


export interface ColumnContextActions {
  onInlineSave?: () => void;
  onInlineCancel?: () => void;
  onRowActions?: (row: any) => void;
  onAttachFile?: (field: TableField) => void;
  onOpenBlob?: (field: TableField, value: any) => void;
  onOpenActionMenu?: (row: any, anchor: HTMLElement) => void;
}

/**
 * Generates ag-Grid column definitions from backend table field metadata.
 */
export function buildColumnDefs(
  fields: TableField[],
  contextActions?: ColumnContextActions,
  visibleFieldIDs?: string[],
  flagRules?: TenantFlagRule[]
): ColDef[] {
  const colDefs: ColDef[] = [];

  // Build field name to ID map
  const fieldNameToIDMap = fields.reduce((map, f) => {
    map[f.fieldName] = f.fieldID;
    return map;
  }, {} as Record<string, string>);

  for (const field of fields) {
    const {
      fieldID,
      fieldName,
      dataType = "",
      readOnly = false,
      listValues,
    } = field;

    if (visibleFieldIDs && !visibleFieldIDs.includes(fieldID)) continue;

    const isBlob = dataType === "Blob" || dataType === "File";
    const editable = !readOnly;
    const upperType = dataType.toUpperCase();

    const colDef: ColDef = {
      field: fieldID,
      headerName: field.required ? `${fieldName} *` : fieldName,
      editable,
      sortable: true,
      filter: true,
      minWidth: 150,
      tooltipField: fieldID,
      tooltipValueGetter: (params) => {
        const raw = formatFieldValue(params.value, field);
        return raw.length > 255 ? raw.substring(0, 255) + "…" : raw;
      },
      cellRenderer: (params: ICellRendererParams) => {
        const isEditMode = params.data?.__isNew;
        const value = params.value;

        if (isBlob && !isEditMode && value) {
          const [extOrType, guidPart] = value.includes(":")
            ? value.split(":")
            : [null, value];


            
          const mimeType = getMimeTypeFromExtension(extOrType);
          const icon = getBlobLabel(mimeType);
          const label = icon ?? extOrType?.toUpperCase() ?? "View File";
          
          const tooltip = mimeType || "Unknown file";

          let foregroundColor: string | undefined;

          if (flagRules && flagRules.length > 0) {
            const matchedRule = flagRules.find(rule => {
              const fieldID = fieldNameToIDMap[rule.fieldName];
              if (!fieldID) return false;
              const fieldVal = params.data?.[fieldID];
              const ruleVal = rule.comparisonValue ?? "";
          
              if (ruleVal === "") {
                switch (rule.comparisonOperator) {
                  case "=": return fieldVal === "" || fieldVal == null;
                  case "!=": return fieldVal != null && String(fieldVal).trim() !== "";
                  case ">=":
                  case "<=": return false;
                  case "not contains": return true;
                  case "is blank": return fieldVal == null || String(fieldVal).trim() === "";
                  default: return false;
                }
              }
          
              switch (rule.comparisonOperator) {
                case "=": return String(fieldVal) == ruleVal;
                case "!=": return String(fieldVal) != ruleVal;
                case ">": return fieldVal > ruleVal;
                case "<": return fieldVal < ruleVal;
                case ">=": return fieldVal >= ruleVal;
                case "<=": return fieldVal <= ruleVal;
                case "contains": return String(fieldVal).includes(ruleVal);
                case "not contains": return !String(fieldVal).includes(ruleVal);
                case "is blank": return fieldVal == null || String(fieldVal).trim() === "";
                default: return false;
              }
            });
          
            if (matchedRule?.rowColor) {
              foregroundColor = getForegroundColor(matchedRule.rowColor);
            }
          }
          
          return (
            <Tooltip title={tooltip}>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  color: foregroundColor,
                  borderColor: foregroundColor,
                  '& .MuiButton-startIcon': { color: foregroundColor },
                }}
                onClick={() => contextActions?.onOpenBlob?.(field, value)}
              >
                {label}
              </Button>
            </Tooltip>
          );
          
          
        }

        return (
          <Tooltip title={formatFieldValue(value, field)} enterDelay={300}>
            <span>{formatFieldValue(value, field)}</span>
          </Tooltip>
        );
      },
    };

    try {
      if (listValues) {
        const options = JSON.parse(listValues);
        if (Array.isArray(options)) {
          if (field.maxChoices && field.maxChoices > 1) {
            colDef.cellRenderer = (params: { value: any; }) => {
              const vals = String(params.value ?? "")
                .split(";")
                .map((s) => s.trim())
                .filter(Boolean);
          
              return (
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {vals.map((val, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        backgroundColor: "#ddd",
                        borderRadius: "8px",
                        px: 1,
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {val}
                    </Box>
                  ))}
                </Box>
              );
            };
          } else {
            colDef.cellEditor = "agSelectCellEditor";
            colDef.cellEditorParams = { values: options };
          }
          
        }
      }
    } catch {
      console.warn(`Invalid listValues for field ${fieldName}:`, listValues);
    }

if (["BOOLEAN", "CHECKBOX"].includes(upperType)) {
  colDef.cellEditor = undefined;

  colDef.cellRenderer = (params: ICellRendererParams) => {
    const val = String(params.value ?? "").toLowerCase();
    const isEditing = params.api.getEditingCells().some(
      (cell) =>
        cell.rowIndex === params.node.rowIndex &&
        cell.column?.getColId() === params.column?.getColId()
    );

    const setVal = (newVal: boolean) => {
      params.node.setDataValue(fieldID, newVal ? "true" : "false");
    };

    let labelTrue = "✅";
    let labelFalse = "❌";

    try {
      const custom = listValues ? JSON.parse(listValues) : [];
      if (Array.isArray(custom) && custom.length >= 2) {
        labelTrue = custom[0];
        labelFalse = custom[1];
      }
    } catch {}

    if (!isEditing) {
      return val === "true" || val === "1"
        ? labelTrue
        : val === "false" || val === "0"
        ? labelFalse
        : "";
    }

    return (
      <Box display="flex" gap={1} alignItems="center">
        <Button
          size="small"
          variant={val === "true" || val === "1" ? "contained" : "outlined"}
          color="success"
          onClick={() => setVal(true)}
        >
          {labelTrue}
        </Button>
        <Button
          size="small"
          variant={val === "false" || val === "0" ? "contained" : "outlined"}
          color="error"
          onClick={() => setVal(false)}
        >
          {labelFalse}
        </Button>
        {(val === "true" || val === "false" || val === "1" || val === "0") && (
          <Button
            size="small"
            onClick={() => params.node.setDataValue(fieldID, "")}
            sx={{ minWidth: 24, color: "gray" }}
          >
            ✖
          </Button>
        )}
      </Box>
    );
  };
}




    if (["DATE", "TIME", "DATETIME", "TIMESTAMP"].includes(upperType)) {
      colDef.editable = true;
    
      const inputType =
        upperType === "DATE"
          ? "date"
          : upperType === "TIME"
          ? "time"
          : "datetime-local";
    
      colDef.cellRenderer = (params: ICellRendererParams) => {
        const val = params.value;
        const isEditing =
          params.api.getEditingCells().some(
            (cell) =>
              cell.rowIndex === params.node.rowIndex &&
              cell.column?.getColId() === params.column?.getColId()
          );
    
        const isValidDate = (v: string) => {
          const d = new Date(v);
          return !isNaN(d.getTime());
        };
    
        if (!isEditing) {
          if (!val) return "";
          return isValidDate(val) ? (
            <span>{formatFieldValue(val, field)}</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>{val}</span>
          );
        }
    
        // When editing:
        const originalVal = val ?? "";
        const inputRef = React.createRef<HTMLInputElement>();
    
        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          const newVal = e.target.value;
          if (!newVal || isValidDate(newVal)) {
            params.node.setDataValue(fieldID, newVal || "");
          } else {
            params.node.setDataValue(fieldID, originalVal); // Revert if invalid
          }
        };
    
        const handleClear = () => {
          params.node.setDataValue(fieldID, "");
        };
    
        const handleOpenPicker = () => {
          inputRef.current?.showPicker?.();
          inputRef.current?.focus();
        };
    
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <input
              type={inputType}
              ref={inputRef}
              defaultValue={val}
              onBlur={handleBlur}
              style={{
                width: "100%",
                border: "1px solid #ccc",
                padding: "4px 8px",
                borderRadius: 4,
                font: "inherit",
              }}
            />
            <Tooltip title="Open Picker">
              <Button
                size="small"
                onClick={handleOpenPicker}
                sx={{ minWidth: 24, px: 0 }}
              >
                {inputType === "time" ? "🕒" : "📅"}
              </Button>
            </Tooltip>
            {!!val && (
              <Tooltip title="Clear">
                <Button
                  size="small"
                  onClick={handleClear}
                  sx={{ minWidth: 24, px: 0, color: "red" }}
                >
                  ✖
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      };
    
      colDef.valueFormatter = (params) => {
        const val = params.value;
        if (!val) return "";
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        if (upperType === "DATE") return d.toLocaleDateString();
        if (upperType === "TIME") return d.toLocaleTimeString();
        return d.toLocaleString();
      };
    }
    
    

    colDefs.push(colDef);
  }

  // === Add Flag Icon Column ===
  if (flagRules?.length) {
    colDefs.unshift({
      headerName: "",
      field: "__flagIcon",
      width: 60,
      maxWidth: 60,
      pinned: "left",
      suppressSizeToFit: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => {
        const row = params.data;
        if (!row) return null;

        const matchedRule = flagRules.find(rule => {
          const fieldID = fieldNameToIDMap[rule.fieldName];
          if (!fieldID) return false;

          const fieldVal = row[fieldID];
          const ruleVal = rule.comparisonValue ?? "";

          if (ruleVal === "") {
            switch (rule.comparisonOperator) {
              case "=": return fieldVal === "" || fieldVal == null;
              case "!=": return fieldVal != null && String(fieldVal).trim() !== "";
              case ">=":
              case "<=": return false;
              case "not contains": return true;
              case "is blank": return fieldVal == null || String(fieldVal).trim() === "";
              default: return false;
            }
          }

          switch (rule.comparisonOperator) {
            case "=": return String(fieldVal) == ruleVal;
            case "!=": return String(fieldVal) != ruleVal;
            case ">": return fieldVal > ruleVal;
            case "<": return fieldVal < ruleVal;
            case ">=": return fieldVal >= ruleVal;
            case "<=": return fieldVal <= ruleVal;
            case "contains": return String(fieldVal).includes(ruleVal);
            case "not contains": return  !String(fieldVal).includes(ruleVal);
            case "is blank": return fieldVal == null || String(fieldVal).trim() === "";
            default: return false;
          }
        });

        if (matchedRule?.iconName && iconMap[matchedRule.iconName]) {
          return (
            <Tooltip title={matchedRule.tooltipText || ""}>
              <Box width={50} display="flex" justifyContent="center" alignItems="center">
                {iconMap[matchedRule.iconName]}
              </Box>
            </Tooltip>
          );
        }

        return null;
      }
    });
  }

  // === Actions Column ===
  colDefs.push({
    headerName: "Actions",
    field: "__actions",
    pinned: "right",
    width: 80,
    sortable: false,
    filter: false,
    suppressMovable: true,
    cellRenderer: (params: ICellRendererParams) => {
      const { data, context } = params;
      if (!data || !data.id) return null;

      return (
        <div onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu
            rowId={data.id}
            rowData={data}
            tableId={context?.tableId}
            handleActionSelect={context?.handleActionSelect}
          />
        </div>
      );
    },
    cellRendererParams: {
      onInlineSave: contextActions?.onInlineSave,
      onInlineCancel: contextActions?.onInlineCancel,
      onRowActions: contextActions?.onRowActions,
    },
  });

  return colDefs;
}

